import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';

// --- HD REVERSIBLE ENCRYPTION UTILITIES ---
const encryptData = (text) => {
    if (!text) return '';
    const encoded = btoa(unescape(encodeURIComponent(text)));
    return `ENC_${encoded}`;
};

const decryptData = (cipherText) => {
    if (!cipherText || !cipherText.startsWith('ENC_')) return '';
    try {
        const actualCipher = cipherText.replace('ENC_', '');
        return decodeURIComponent(escape(atob(actualCipher)));
    } catch (e) {
        console.error("Failed to decrypt signature data:", e);
        return '';
    }
};

// --- HIGH-DEFINITION SIGNATURE PAD WITH SOLID BACKDROP ---
const SignaturePad = ({ value, onChange, onClear, disabled }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Initialize and size canvas dynamically for HD clarity
    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        
        const rect = container.getBoundingClientRect();
        const width = rect.width || 300;
        const height = 120; // Matches your desired proportional size

        // Setup HD buffer dimensions scaled by screen DPI
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        // Keep layout CSS size constant
        canvas.style.width = '100%';
        canvas.style.height = `${height}px`;

        ctx.scale(dpr, dpr);

        // Elegant Pen Configuration
        ctx.strokeStyle = '#000000'; // Pure black ink for formal signatures
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Load and render encrypted database image if present
        if (value) {
            const decryptedDataUrl = decryptData(value);
            if (decryptedDataUrl) {
                const img = new Image();
                img.src = decryptedDataUrl;
                img.onload = () => {
                    ctx.clearRect(0, 0, width, height);
                    ctx.drawImage(img, 0, 0, width, height);
                };
            }
        }
    }, [value]);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e) => {
        if (disabled) return;
        if (e.touches) e.preventDefault();

        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing || disabled) return;
        if (e.touches) e.preventDefault();

        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const width = canvas.width / dpr;
        const height = canvas.height / dpr;

        // 🔥 Seamlessly inject solid white background behind the ink lines
        ctx.save();
        ctx.globalCompositeOperation = "destination-over";
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);
        ctx.restore();

        // Convert the clean output with white background to base64 and encrypt
        const rawBase64Image = canvas.toDataURL('image/png', 1.0);
        const encryptedValue = encryptData(rawBase64Image);

        if (onChange) {
            onChange(encryptedValue);
        }
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const width = canvas.width / dpr;
        const height = canvas.height / dpr;
        
        ctx.clearRect(0, 0, width, height);
        
        if (onChange) onChange('');
        if (onClear) onClear();
    };

    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
            <canvas
                ref={canvasRef}
                className="signatureCanvas"
                style={{
                    display: 'block',
                    border: '1px solid black',
                    borderRadius: '10px',
                    background: '#ffffff',
                    cursor: disabled ? 'default' : 'crosshair',
                    touchAction: 'none'
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
            />

            {disabled && !value && (
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(245, 245, 245, 0.85)',
                    borderRadius: '10px'
                }}>
                    <Typography variant="caption" sx={{ color: '#777', fontWeight: '500' }}>
                        No Signature Capture
                    </Typography>
                </Box>
            )}

            {!disabled && value && (
                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={clearCanvas}
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        fontSize: '10px',
                        padding: '2px 6px',
                        textTransform: 'none',
                        zIndex: 10
                    }}
                >
                    Clear
                </Button>
            )}
        </Box>
    );
};

export default SignaturePad;